import React from "react";
import styled, { css } from "styled-components";

import Graph from "src/library/components/Graph";
import {
  OrderTotalsResult,
  useWidgetDataApi
} from "src/core/dashboard/api/widget-data.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { GraphPayload } from "src/library/components/Graph/GraphToolTip";
import { useWidgetContainerSize } from "src/core/dashboard/hooks";
import { WidgetDisplayTypes } from "src/core/dashboard/api/dashboard.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import {
  getDisplayRange,
  getGraphMetrics,
  DisplayRangeType,
  getSelectedWidgetView,
  WidgetProps
} from "../utils";
import DataLoadError from "../DataLoadError";
import { groupOrderTotals } from "./utils";
import { NumberWidget } from "../NumberWidget";
import { WidgetInnerHeader } from "../WidgetInnerHeader";
import { GraphDataType } from "../WidgetTotalValueSwitcher";
import { useWidgetFontSize } from "../hooks";

// Type for the flat object used to display data in graph
export interface OrderTotalsGraphValue extends Omit<GraphPayload, "date"> {
  date: Date;
  value: number;
}

const ORDER_TOTALS_CACHE_KEY = "widget-order-totals";

/**
 * Returns order totals widget content
 */
export const OrderTotalsWidget: React.FunctionComponent<WidgetProps> = ({
  options, filters, widgetId, widgetTypeId, ignoreCache = false
}) => {
  const [ displayRange, setDisplayRange ] = React.useState<DisplayRangeType>("day");
  const [ graphDataType, setGraphDataType ] = React.useState<GraphDataType>("value");
  const [ orderDataResponse, orderDataRequest ] = useWidgetDataApi("GET_ORDER_TOTALS");
  const wrapperRef = React.createRef<HTMLDivElement>();
  const { width } = useWidgetContainerSize(wrapperRef);
  const fontSize = useWidgetFontSize(width, options.view);

  // On load, get the data we need
  React.useEffect(() => {
    orderDataRequest({
      widgetTypeId,
      filters,
      options: null
    }, {
      cache: {
        maxAge: cacheMinutesToMilliseconds(60),
        key: () => ORDER_TOTALS_CACHE_KEY,
        ignoreCache
      }
    });
  }, [
    filters,
    ignoreCache,
    orderDataRequest,
    widgetTypeId
  ]);

  const selectedView: string = React.useMemo(() => {
    return getSelectedWidgetView(options.view);
  }, [ options ]);

  // Takes the raw response from API and groups based on range between first and last date in data
  const transformToGraphData = React.useCallback((orderTotalsResult: OrderTotalsResult): OrderTotalsGraphValue[] => {
    // Find range to determine whether to display in days, weeks or months
    const calculatedDisplayRange = getDisplayRange(orderTotalsResult);

    setDisplayRange(calculatedDisplayRange);
    // Group, accumulate totals and return

    return groupOrderTotals(orderTotalsResult, calculatedDisplayRange, graphDataType);
  }, [ graphDataType ]);

  // The data used to graph view widget
  const graphDisplayData = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      if (orderDataResponse.data?.result) {
        return transformToGraphData(orderDataResponse.data?.result);
      }
    }

    return null;
  }, [
    orderDataResponse.data?.result,
    transformToGraphData,
    selectedView
  ]);

  // The data used for number view widget
  const numberDisplayData = React.useMemo(() => {
    if (selectedView === WidgetDisplayTypes.Number) {
      const orderTotalResult = orderDataResponse.data?.result;

      if (orderTotalResult) {
        return {
          value: orderTotalResult.reduce((total, orderTotal) => total + orderTotal.value, 0),
          total: orderTotalResult.reduce((total, orderTotal) => total + orderTotal.count, 0)
        };
      }
    }

    return null;
  }, [ orderDataResponse.data?.result, selectedView ]);

  const graphMetrics = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      return graphDisplayData ? getGraphMetrics(graphDisplayData, [ "date" ]) : [];
    }

    return null;
  }, [ graphDisplayData, selectedView ]);

  if (selectedView !== "bar" && selectedView !== "line" && selectedView !== WidgetDisplayTypes.Number) {
    // Currently, graph bar, line and number are the only handled view options availble below
    return null;
  }

  return (
    <StyledWrapper ref={wrapperRef}>
      {orderDataResponse.pending && (
        <LoadingSpinner type="circular" />
      )}

      {graphDisplayData && graphMetrics && !orderDataResponse.error && (selectedView === "line" || selectedView === "bar") && (
        <>
          <WidgetInnerHeader
            title="Order Totals"
            containerWidth={width}
            graphDataType={graphDataType}
            updateGraphDataType={setGraphDataType}
          />

          <Graph
            hideLegend
            containerWidth={width}
            data={graphDisplayData}
            type={selectedView}
            toolTipValueType={graphDataType === "value" ? "price" : "number"}
            graphMetrics={graphMetrics}
            xAxis={{
              dataKey: "date",
              dateFormat: displayRange === "month" ? "month" : "day"
            }}
          />
        </>
      )}

      {numberDisplayData && fontSize && !orderDataResponse.error && (
        <NumberWidget
          widgetId={widgetId}
          displayData={numberDisplayData}
          units="orders"
          valueType="price"
          fontSize={fontSize}
        />
      )}

      {orderDataResponse.error && (
        <DataLoadError errorMessage={orderDataResponse.error.message} />
      )}
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    height: 100%;
    width: 100%;
    padding: ${theme.spacing.padding.sm} 0; 
  `}
`;
